import config from '../../config/config';

// Configuración de la API (como estaba para fetch)
export const API_CONFIG = {
  baseUrl: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// Mostrar la URL de la API en la consola en modo desarrollo
if (config.DEBUG) {
  console.log('API URL:', config.API_URL);
}

// Tipo base para errores de la API
export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Helper para obtener token
const getAuthToken = (): string | null => localStorage.getItem('userToken');
// Helper para logout
const logoutUser = (): void => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userData');
  // Podríamos añadir un evento o redirección aquí si fuera necesario
  // window.dispatchEvent(new CustomEvent('auth:logout'));
};

// Tipo para opciones de fetch (como estaba antes)
interface FetchOptions extends RequestInit {
  requiresAuth?: boolean;
  body?: any; // Puede ser FormData o un objeto para JSON
}

/**
 * Cliente HTTP basado en Fetch con manejo de FormData
 */
export class HttpClient {

  static async fetch<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const { requiresAuth = true, body, headers: optionHeaders, ...fetchOptions } = options;

    const url = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.baseUrl}${endpoint}`;

    // Configurar headers base
    const headers = new Headers(API_CONFIG.headers as HeadersInit);

    // Añadir/Sobrescribir headers de las opciones
    if (optionHeaders) {
      Object.entries(optionHeaders).forEach(([key, value]) => {
        headers.set(key, value as string);
      });
    }

    // Añadir token si es necesario
    if (requiresAuth) {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Configurar el cuerpo y Content-Type
    const requestOptions: RequestInit = {
      ...fetchOptions,
      headers,
    };

    if (body) {
      if (body instanceof FormData) {
        // Si es FormData, no establecer Content-Type (el navegador lo hace)
        // y pasar el body directamente
        headers.delete('Content-Type');
        requestOptions.body = body;
      } else {
        // Si no es FormData, asumir JSON
        requestOptions.body = JSON.stringify(body);
        // Asegurar que el Content-Type sea application/json si no se especificó otro
        if (!headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
      }
    }

    // Realizar la petición
    const response = await fetch(url, requestOptions);

    // ... (Manejo de error 401 y otros errores como estaba antes) ...
    if (response.status === 401) {
      logoutUser();
      const authError = new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      (authError as any).isAuthError = true;
      const authErrorEvent = new CustomEvent('auth:error', {
        detail: { message: authError.message }
      });
      window.dispatchEvent(authErrorEvent);
      throw authError;
    }

    if (!response.ok) {
      const errorResponse = await response.text();
      let errorMessage = `Error del servidor: ${response.status} - ${response.statusText}`;
      let errorData: any = null;
      try {
        errorData = JSON.parse(errorResponse);
        errorMessage = errorData?.message || errorMessage;
      } catch (e) {
        errorMessage = errorResponse || errorMessage;
      }
      throw new ApiError(errorMessage, response.status, errorData);
    }

    // Intentar parsear como JSON si hay contenido
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      // Si es 204 No Content o no hay contenido, devolver null o un objeto vacío según se necesite
      // Para ser consistentes, podríamos devolver null
      return null as T;
    }

    try {
      return await response.json() as T;
    } catch (error) {
      console.error("Failed to parse JSON response", error);
      throw new Error('Error al parsear la respuesta JSON del servidor');
    }
  }

  // Métodos GET, POST, PUT, PATCH, DELETE usan el this.fetch actualizado
  static async get<T>(endpoint: string, options: Omit<FetchOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'GET' });
  }

  static async post<T>(endpoint: string, body: any, options: Omit<FetchOptions, 'method'> = {}): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'POST', body });
  }

  static async put<T>(endpoint: string, body: any, options: Omit<FetchOptions, 'method'> = {}): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'PUT', body });
  }

  static async patch<T>(endpoint: string, body: any, options: Omit<FetchOptions, 'method'> = {}): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  static async delete<T>(endpoint: string, options: Omit<FetchOptions, 'method' | 'body'> = {}): Promise<T> {
    // Para DELETE, establecer explícitamente Content-Type a text/plain
    const deleteHeaders = {
      ...(options.headers || {}),
      'Content-Type': 'text/plain' // Establecer explícitamente
    };

    // Crear una nueva instancia de opciones para no modificar la original
    const deleteOptions = { ...options, headers: deleteHeaders };

    // Ajustar el tipo esperado si delete puede devolver null (por 204)
    return this.fetch<T | null>(endpoint, { ...deleteOptions, method: 'DELETE' }) as Promise<T>;
  }
}

// La función secureApiCall sigue siendo útil para envolver las llamadas en los servicios
export async function secureApiCall<T>(
  apiCall: () => Promise<T>,
  errorHandler?: (error: any) => T
): Promise<T> {
  try {
    return await apiCall();
  } catch (error: any) {
    console.error('API call error caught by secureApiCall:', error);

    // Comprobar si es error de autenticación marcado por el interceptor
    if (error.isAuthError) {
      // El interceptor ya hizo logout y lanzó evento, solo re-lanzamos
      throw error;
    }

    if (errorHandler) {
      return errorHandler(error);
    }
    throw error;
  }
}
